/**
 * Delivery Effect Renderer
 *
 * Renders visual effects based on ability delivery type.
 * Each delivery type has distinct animation patterns.
 */

import * as PIXI from 'pixi.js';
import { getElementConfig, type ElementType } from './element-effect-configs.js';

export type DeliveryType = 'melee' | 'projectile' | 'breath' | 'beam' | 'aoe' | 'buff' | 'strike' | 'breath-particle';

export interface DeliveryEffectParams {
  container: PIXI.Container;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  element: ElementType;
  onComplete?: () => void;
}

export interface AOEEffectParams {
  container: PIXI.Container;
  centerX: number;
  centerY: number;
  radius: number;  // In pixels
  element: ElementType;
  onComplete?: () => void;
}

export interface BuffEffectParams {
  container: PIXI.Container;
  targetX: number;
  targetY: number;
  element: ElementType;
  isBuff: boolean;  // true = buff (particles go up), false = debuff (particles go down)
  onComplete?: () => void;
}

// L3-LIGHTNING-SKY: Strike effect params for lightning from sky
export interface StrikeEffectParams {
  container: PIXI.Container;
  targetX: number;
  targetY: number;
  element: ElementType;
  strikeOrigin: 'sky' | 'ground';
  screenHeight: number;  // Used to calculate strike start position
  onComplete?: () => void;
}

/**
 * Render melee slash effect
 * Quick arc animation at target position
 */
export function renderMeleeEffect(params: DeliveryEffectParams): void {
  const { container, targetX, targetY, element, onComplete } = params;
  const config = getElementConfig(element);

  // Create slash arc graphic
  const slash = new PIXI.Graphics();
  slash.position.set(targetX, targetY);
  slash.alpha = 0;
  slash.blendMode = config.blendMode as any;
  container.addChild(slash);

  // Animation variables
  let frame = 0;
  const totalFrames = 12;
  const arcRadius = 30;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Draw arc that expands
    slash.clear();
    slash.lineStyle(4, config.primaryColor, 1 - progress * 0.5);

    const startAngle = -Math.PI / 4;
    const endAngle = Math.PI / 4;
    const currentAngle = startAngle + (endAngle - startAngle) * Math.min(progress * 1.5, 1);

    slash.arc(0, 0, arcRadius * (0.5 + progress * 0.5), startAngle, currentAngle);
    slash.alpha = 1 - progress;
    slash.scale.set(1 + progress * 0.5);

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(slash);
      slash.destroy();
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render projectile traveling from start to target
 */
export function renderProjectileEffect(params: DeliveryEffectParams): void {
  const { container, startX, startY, targetX, targetY, element, onComplete } = params;
  const config = getElementConfig(element);

  // Create projectile sprite
  const projectile = new PIXI.Graphics();
  projectile.beginFill(config.primaryColor);

  // Shape based on config
  if (config.projectileShape === 'ball' || config.projectileShape === 'orb') {
    projectile.drawCircle(0, 0, 8 * config.projectileScale);
  } else if (config.projectileShape === 'shard') {
    projectile.drawPolygon([0, -12, 4, 0, 0, 12, -4, 0]);
  } else if (config.projectileShape === 'bolt') {
    projectile.drawRect(-2, -10, 4, 20);
  } else {
    projectile.drawCircle(0, 0, 6 * config.projectileScale);
  }
  projectile.endFill();

  // Add glow
  if (config.glowIntensity > 0) {
    const glow = new PIXI.Graphics();
    glow.beginFill(config.glowColor, config.glowIntensity * 0.3);
    glow.drawCircle(0, 0, 16 * config.projectileScale);
    glow.endFill();
    glow.blendMode = 'add';
    projectile.addChildAt(glow, 0);
  }

  projectile.position.set(startX, startY);
  projectile.blendMode = config.blendMode as any;
  container.addChild(projectile);

  // Calculate trajectory
  const dx = targetX - startX;
  const dy = targetY - startY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const duration = Math.max(200, distance * 2); // ms
  const startTime = performance.now();

  // Rotate to face direction
  if (config.projectileRotates) {
    projectile.rotation = Math.atan2(dy, dx);
  }

  const animate = () => {
    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    projectile.x = startX + dx * progress;
    projectile.y = startY + dy * progress;

    if (config.projectileRotates) {
      projectile.rotation += 0.2;
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(projectile);
      projectile.destroy({ children: true });
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render breath cone effect
 * Expanding cone from attacker toward target
 */
export function renderBreathEffect(params: DeliveryEffectParams): void {
  const { container, startX, startY, targetX, targetY, element, onComplete } = params;
  const config = getElementConfig(element);

  const breath = new PIXI.Graphics();
  breath.position.set(startX, startY);
  breath.blendMode = config.blendMode as any;
  container.addChild(breath);

  const angle = Math.atan2(targetY - startY, targetX - startX);
  breath.rotation = angle;

  let frame = 0;
  const totalFrames = 30;
  const maxLength = 80;
  const coneAngle = Math.PI / 6; // 30 degree cone

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    breath.clear();

    // Draw cone
    const length = maxLength * Math.min(progress * 2, 1);
    const alpha = progress < 0.5 ? 1 : 1 - (progress - 0.5) * 2;

    breath.beginFill(config.primaryColor, alpha * 0.6);
    breath.moveTo(0, 0);
    breath.lineTo(
      Math.cos(-coneAngle) * length,
      Math.sin(-coneAngle) * length
    );
    breath.lineTo(
      Math.cos(coneAngle) * length,
      Math.sin(coneAngle) * length
    );
    breath.closePath();
    breath.endFill();

    // Inner brighter cone
    breath.beginFill(config.secondaryColor, alpha * 0.4);
    breath.moveTo(0, 0);
    breath.lineTo(
      Math.cos(-coneAngle * 0.5) * length * 0.8,
      Math.sin(-coneAngle * 0.5) * length * 0.8
    );
    breath.lineTo(
      Math.cos(coneAngle * 0.5) * length * 0.8,
      Math.sin(coneAngle * 0.5) * length * 0.8
    );
    breath.closePath();
    breath.endFill();

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(breath);
      breath.destroy();
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render beam effect
 * Instant line from attacker to target with flash
 */
export function renderBeamEffect(params: DeliveryEffectParams): void {
  const { container, startX, startY, targetX, targetY, element, onComplete } = params;
  const config = getElementConfig(element);

  const beam = new PIXI.Graphics();
  beam.blendMode = config.blendMode as any;
  container.addChild(beam);

  let frame = 0;
  const totalFrames = 15;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    beam.clear();

    // Beam width pulses then fades
    const width = frame < 3 ? 8 + frame * 2 : 12 * (1 - progress);
    const alpha = progress < 0.3 ? 1 : 1 - (progress - 0.3) / 0.7;

    // Outer glow
    beam.lineStyle(width + 4, config.glowColor, alpha * 0.3);
    beam.moveTo(startX, startY);
    beam.lineTo(targetX, targetY);

    // Main beam
    beam.lineStyle(width, config.primaryColor, alpha);
    beam.moveTo(startX, startY);
    beam.lineTo(targetX, targetY);

    // Core
    beam.lineStyle(width * 0.3, config.secondaryColor, alpha);
    beam.moveTo(startX, startY);
    beam.lineTo(targetX, targetY);

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(beam);
      beam.destroy();
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render AOE circle effect
 * Expanding ring at target location
 */
export function renderAOEEffect(params: AOEEffectParams): void {
  const { container, centerX, centerY, radius, element, onComplete } = params;
  const config = getElementConfig(element);

  const aoe = new PIXI.Graphics();
  aoe.position.set(centerX, centerY);
  aoe.blendMode = config.blendMode as any;
  container.addChild(aoe);

  let frame = 0;
  const totalFrames = 25;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    aoe.clear();

    const currentRadius = radius * Math.min(progress * 1.5, 1);
    const alpha = progress < 0.4 ? 1 : 1 - (progress - 0.4) / 0.6;

    // Outer ring
    aoe.lineStyle(3, config.primaryColor, alpha);
    aoe.drawCircle(0, 0, currentRadius);

    // Fill
    aoe.beginFill(config.primaryColor, alpha * 0.2);
    aoe.drawCircle(0, 0, currentRadius);
    aoe.endFill();

    // Inner ring
    aoe.lineStyle(2, config.secondaryColor, alpha * 0.5);
    aoe.drawCircle(0, 0, currentRadius * 0.6);

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(aoe);
      aoe.destroy();
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render buff/debuff effect
 * Particles rising (buff) or falling (debuff) around target
 */
export function renderBuffEffect(params: BuffEffectParams): void {
  const { container, targetX, targetY, element, isBuff, onComplete } = params;
  const config = getElementConfig(element);

  const particleContainer = new PIXI.Container();
  particleContainer.position.set(targetX, targetY);
  container.addChild(particleContainer);

  // Create particles
  const particles: { sprite: PIXI.Graphics; vx: number; vy: number; life: number }[] = [];
  const particleCount = 12;

  for (let i = 0; i < particleCount; i++) {
    const p = new PIXI.Graphics();
    p.beginFill(config.primaryColor);
    p.drawCircle(0, 0, 3);
    p.endFill();
    p.blendMode = config.blendMode as any;

    // Random position around target
    const angle = (i / particleCount) * Math.PI * 2;
    const dist = 15 + Math.random() * 10;
    p.x = Math.cos(angle) * dist;
    p.y = Math.sin(angle) * dist;

    particleContainer.addChild(p);

    particles.push({
      sprite: p,
      vx: (Math.random() - 0.5) * 2,
      vy: isBuff ? -(2 + Math.random() * 2) : (2 + Math.random() * 2),
      life: 30 + Math.random() * 20
    });
  }

  let frame = 0;
  const maxFrames = 60;

  const animate = () => {
    frame++;

    for (const p of particles) {
      if (p.life > 0) {
        p.sprite.x += p.vx;
        p.sprite.y += p.vy;
        p.life--;
        p.sprite.alpha = p.life / 50;
      }
    }

    if (frame < maxFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(particleContainer);
      particleContainer.destroy({ children: true });
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render breath particle effect - massive fan of projectiles (L3-PARTICLE-BREATH)
 * Creates 25 individual fireballs spreading in a 60° arc for devastating visual impact
 */
export function renderBreathParticleEffect(params: DeliveryEffectParams): void {
  const { container, startX, startY, targetX, targetY, element, onComplete } = params;
  const config = getElementConfig(element);
  const projectileCount = 25;

  // Calculate direction to target
  const dx = targetX - startX;
  const dy = targetY - startY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const baseAngle = Math.atan2(dy, dx);

  // Track completed projectiles
  let completedCount = 0;

  for (let i = 0; i < projectileCount; i++) {
    // Stagger spawning by 20ms intervals
    setTimeout(() => {
      // Calculate spread angle within 60° arc
      const normalizedIndex = projectileCount > 1
        ? i / (projectileCount - 1)
        : 0.5;
      const spreadAngle = (normalizedIndex - 0.5) * (Math.PI / 3); // 60° = π/3 radians
      const projectileAngle = baseAngle + spreadAngle;

      // Add slight random variation for organic feel
      const seed = i * 12345;
      const variation = ((seed * 9301 + 49297) % 233280) / 233280 - 0.5;
      const angleVariation = variation * 0.1;
      const finalAngle = projectileAngle + angleVariation;

      // Calculate target position for this projectile
      const projectileDistance = distance * (0.9 + variation * 0.2); // 80-100% of target distance
      const projTargetX = startX + Math.cos(finalAngle) * projectileDistance;
      const projTargetY = startY + Math.sin(finalAngle) * projectileDistance;

      // Create projectile graphic
      const projectile = new PIXI.Graphics();

      // Vary projectile size (80-120% of base)
      const sizeVariation = 0.8 + Math.random() * 0.4;
      const baseSize = 8;
      const size = baseSize * sizeVariation;

      // Draw projectile - main body
      projectile.circle(0, 0, size);
      projectile.fill({ color: config.primaryColor, alpha: 0.9 });

      // Add glow
      const glow = new PIXI.Graphics();
      glow.circle(0, 0, size * 1.8);
      glow.fill({ color: config.glowColor, alpha: 0.4 });
      glow.blendMode = 'add' as PIXI.BLEND_MODES;
      projectile.addChild(glow);

      // Position at start
      projectile.position.set(startX, startY);
      projectile.blendMode = config.blendMode as PIXI.BLEND_MODES;
      container.addChild(projectile);

      // Animate projectile with arc trajectory
      const flightDuration = 400 + Math.random() * 100; // 400-500ms
      const arcHeight = 15 + Math.random() * 10; // Slight arc
      const projectileStartTime = performance.now();

      const animateProjectile = () => {
        const elapsed = performance.now() - projectileStartTime;
        const progress = Math.min(elapsed / flightDuration, 1);

        // Linear interpolation for X and Y
        const currentX = startX + (projTargetX - startX) * progress;
        let currentY = startY + (projTargetY - startY) * progress;

        // Add arc (parabolic)
        const arcOffset = -4 * arcHeight * progress * (progress - 1);
        currentY -= arcOffset;

        projectile.position.set(currentX, currentY);

        // Fade out near end
        if (progress > 0.7) {
          const fadeProgress = (progress - 0.7) / 0.3;
          projectile.alpha = 1 - fadeProgress;
        }

        // Rotate to face direction of travel
        if (progress < 1) {
          const nextProgress = Math.min(progress + 0.05, 1);
          const nextX = startX + (projTargetX - startX) * nextProgress;
          let nextY = startY + (projTargetY - startY) * nextProgress;
          const nextArcOffset = -4 * arcHeight * nextProgress * (nextProgress - 1);
          nextY -= nextArcOffset;
          projectile.rotation = Math.atan2(nextY - currentY, nextX - currentX);
        }

        if (progress < 1) {
          requestAnimationFrame(animateProjectile);
        } else {
          // Cleanup
          container.removeChild(projectile);
          projectile.destroy({ children: true });

          completedCount++;
          // Call onComplete when all projectiles finish
          if (completedCount === projectileCount) {
            onComplete?.();
          }
        }
      };

      requestAnimationFrame(animateProjectile);
    }, i * 20); // 20ms stagger
  }
}

/**
 * Generate a jagged lightning path using midpoint displacement (L3-LIGHTNING-SKY)
 * Creates organic-looking lightning bolts
 */
function generateJaggedPath(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  jaggedness: number = 0.15,
  depth: number = 4
): Array<{ x: number; y: number }> {
  let points = [
    { x: startX, y: startY },
    { x: endX, y: endY }
  ];

  for (let d = 0; d < depth; d++) {
    const newPoints: Array<{ x: number; y: number }> = [];

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      newPoints.push(p1);

      // Calculate midpoint with perpendicular offset
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 0) {
        const offset = dist * jaggedness * (Math.random() - 0.5) * 2;
        newPoints.push({
          x: mx + (-dy / dist) * offset,
          y: my + (dx / dist) * offset
        });
      } else {
        newPoints.push({ x: mx, y: my });
      }
    }

    newPoints.push(points[points.length - 1]);
    points = newPoints;
    jaggedness *= 0.6; // Reduce jaggedness at finer detail levels
  }

  return points;
}

/**
 * Draw a path on a PIXI.Graphics object (L3-LIGHTNING-SKY)
 */
function drawLightningPath(
  graphics: PIXI.Graphics,
  path: Array<{ x: number; y: number }>
): void {
  if (path.length < 2) return;

  graphics.moveTo(path[0].x, path[0].y);
  for (let i = 1; i < path.length; i++) {
    graphics.lineTo(path[i].x, path[i].y);
  }
  graphics.stroke();
}

/**
 * Render strike effect - lightning from sky (L3-LIGHTNING-SKY)
 * Creates dramatic jagged lightning bolt from screen top to target
 */
export function renderStrikeEffect(params: StrikeEffectParams): void {
  const { container, targetX, targetY, element, strikeOrigin, screenHeight, onComplete } = params;
  const config = getElementConfig(element);

  // Calculate start position based on strike origin
  let startY: number;
  if (strikeOrigin === 'sky') {
    // Start from above the visible area (negative Y in world space)
    startY = targetY - screenHeight * 0.8;
  } else {
    // Ground strike - start from below
    startY = targetY + screenHeight * 0.3;
  }

  const lightning = new PIXI.Graphics();
  lightning.blendMode = 'add' as any;
  container.addChild(lightning);

  // Generate the jagged lightning path
  const mainPath = generateJaggedPath(targetX, startY, targetX, targetY, 0.15, 4);

  // Generate 2-3 branch paths for extra drama
  const branches: Array<{ path: Array<{ x: number; y: number }>; opacity: number }> = [];
  const branchCount = 2 + Math.floor(Math.random() * 2); // 2-3 branches

  for (let b = 0; b < branchCount; b++) {
    // Pick a random point along the main path (30%-70% down)
    const branchStartIdx = Math.floor(mainPath.length * (0.3 + Math.random() * 0.4));
    const branchStart = mainPath[branchStartIdx];

    // Branch endpoint - offset from main bolt
    const branchLength = (targetY - startY) * (0.3 + Math.random() * 0.2);
    const branchAngle = (Math.random() - 0.5) * Math.PI * 0.5; // -45 to +45 degrees
    const branchEndX = branchStart.x + Math.sin(branchAngle) * branchLength * 0.5;
    const branchEndY = branchStart.y + Math.cos(branchAngle) * branchLength;

    branches.push({
      path: generateJaggedPath(branchStart.x, branchStart.y, branchEndX, branchEndY, 0.2, 3),
      opacity: 0.3 + Math.random() * 0.2 // 30-50% opacity
    });
  }

  let frame = 0;
  const totalFrames = 25;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    lightning.clear();

    // Calculate alpha - bright flash on first 3 frames, then fade
    const baseAlpha = frame < 3 ? 1 : Math.max(0, 1 - (progress - 0.12) * 1.2);

    // Draw branches first (behind main bolt)
    for (const branch of branches) {
      // Outer glow
      lightning.setStrokeStyle({ width: 8, color: config.glowColor, alpha: baseAlpha * branch.opacity * 0.3 });
      drawLightningPath(lightning, branch.path);

      // Main branch
      lightning.setStrokeStyle({ width: 3, color: config.primaryColor, alpha: baseAlpha * branch.opacity });
      drawLightningPath(lightning, branch.path);
    }

    // Draw main bolt - outer glow
    lightning.setStrokeStyle({ width: 16, color: config.glowColor, alpha: baseAlpha * 0.4 });
    drawLightningPath(lightning, mainPath);

    // Main bolt - primary color
    lightning.setStrokeStyle({ width: 6, color: config.primaryColor, alpha: baseAlpha });
    drawLightningPath(lightning, mainPath);

    // Main bolt - inner white core
    lightning.setStrokeStyle({ width: 2, color: 0xFFFFFF, alpha: baseAlpha * 0.9 });
    drawLightningPath(lightning, mainPath);

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(lightning);
      lightning.destroy();
      onComplete?.();
    }
  };

  animate();
}

/**
 * Main function to render effect based on delivery type
 */
export function renderDeliveryEffect(
  deliveryType: DeliveryType,
  params: DeliveryEffectParams | AOEEffectParams | BuffEffectParams | StrikeEffectParams
): void {
  switch (deliveryType) {
    case 'melee':
      renderMeleeEffect(params as DeliveryEffectParams);
      break;
    case 'projectile':
      renderProjectileEffect(params as DeliveryEffectParams);
      break;
    case 'breath':
      renderBreathEffect(params as DeliveryEffectParams);
      break;
    case 'beam':
      renderBeamEffect(params as DeliveryEffectParams);
      break;
    case 'aoe':
      renderAOEEffect(params as AOEEffectParams);
      break;
    case 'buff':
      renderBuffEffect(params as BuffEffectParams);
      break;
    case 'strike':
      renderStrikeEffect(params as StrikeEffectParams);
      break;
    case 'breath-particle':
      renderBreathParticleEffect(params as DeliveryEffectParams);
      break;
  }
}
